/**
 * Supplement mutation hooks
 * 
 * Handles all supplement update operations (status changes, justification edits).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { SupplementStatus } from '@/api/generated/client/models';
import { useToast } from '@/hooks/use-toast';

export function useUpdateSupplement() {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
        mutationFn: async ({ supplementId, status }: { supplementId: number; status: SupplementStatus }) => {
            const response = await apiClient.supplements.updateSupplement({
                supplementId,
                updateSupplementRequest: {
                    status,
                },
            });
            return response;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['supplements'] });
            queryClient.invalidateQueries({ queryKey: ['supplement', variables.supplementId] });
            
            toast({
                title: 'Status updated',
                description: `Supplement status changed to ${variables.status}`,
            });
        },
        onError: (error: any) => {
            toast({
                title: 'Failed to update status',
                description: error.response?.data?.message || 'An error occurred',
                variant: 'destructive',
            });
        },
    });
}

interface UpdateJustificationParams {
    supplementId: number;
    justificationId: number;
    data: {
        justificationText?: string | null;
        codeBookTitle?: string | null;
        chapter?: string | null;
        snippet?: string | null;
    };
}

interface UpsertJustificationParams {
    supplementId: number;
    lineItemMatchGroupId?: number | null;
    lineItemId?: number | null;
    data: {
        justificationText?: string | null;
        codeBookTitle?: string | null;
        chapter?: string | null;
        snippet?: string | null;
    };
}

export function useUpsertJustification() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ supplementId, lineItemMatchGroupId, lineItemId, data }: UpsertJustificationParams) => {
            const result = await apiClient.supplements.upsertSupplementJustification({
                supplementId,
                upsertSupplementJustificationRequest: {
                    lineItemMatchGroupId: lineItemMatchGroupId || null,
                    lineItemId: lineItemId || null,
                    justificationText: data.justificationText?.trim() || null,
                    codeBookTitle: data.codeBookTitle?.trim() || null,
                    chapter: data.chapter?.trim() || null,
                    snippet: data.snippet?.trim() || null,
                },
            });

            return result;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['supplement-detailed', variables.supplementId],
            });
            queryClient.invalidateQueries({
                queryKey: ['supplement', variables.supplementId],
            });
        },
    });
}

export function useUpdateJustification() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ supplementId, justificationId, data }: UpdateJustificationParams) => {
            const payload = {
                justificationText: data.justificationText?.trim() || null,
                codeBookTitle: data.codeBookTitle?.trim() || null,
                chapter: data.chapter?.trim() || null,
                snippet: data.snippet?.trim() || null,
            };

            const result = await apiClient.supplements.updateSupplementJustification({
                supplementId,
                justificationId,
                updateSupplementJustificationRequest: payload,
            });

            return result;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['supplement-detailed', variables.supplementId],
            });
            queryClient.invalidateQueries({
                queryKey: ['supplement', variables.supplementId],
            });
        },
    });
}

export function useDeleteJustification() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ supplementId, justificationId }: { supplementId: number; justificationId: number }) => {
            await apiClient.supplements.deleteSupplementJustification({
                supplementId,
                justificationId,
            });
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: ['supplement-detailed', variables.supplementId],
            });
            queryClient.invalidateQueries({
                queryKey: ['supplement', variables.supplementId],
            });
        },
    });
}
